import { isoDate } from "./dates.js";

/**
 * Is this forecast worth believing?
 *
 * Two questions, and neither is about whether clients pay on time — that is a
 * fact about clients and the accounts already answer it. These are about the
 * forecast itself:
 *
 *   - When a week arrives, does it do what we said it would?
 *   - Before it arrives, how much does what we say about it move?
 *
 * The second matters as much as the first. A forecast that lands on the number
 * but only settles the day before is no use for deciding anything, and a
 * forecast that never moves is not being maintained.
 *
 * All of this is built from snapshots taken as the forecast was believed at the
 * time, so it says nothing until a week has passed. That is the honest answer
 * rather than a number invented from today's data.
 */

const byWeek = (snapshots) => {
  const map = new Map();
  for (const s of snapshots) {
    const list = map.get(s.week_start) || [];
    list.push(s);
    map.set(s.week_start, list);
  }
  for (const list of map.values()) list.sort((a, b) => (a.taken_on < b.taken_on ? -1 : 1));
  return map;
};

const days = (a, b) => Math.round((new Date(a) - new Date(b)) / 86400000);

/**
 * How much the view of a week moved while it was still ahead of us.
 *
 * First seen against latest, so a week that has been quietly sliding shows it.
 */
export function forecastDrift(snapshots = [], weeks = []) {
  const upcoming = new Set(
    weeks.filter((w) => !w.isHistory).map((w) => isoDate(w.start))
  );
  const out = [];
  for (const [week, list] of byWeek(snapshots)) {
    if (!upcoming.has(week) || list.length < 2) continue;
    const first = list[0];
    const latest = list[list.length - 1];
    out.push({
      week,
      firstSeen: first.taken_on,
      latestSeen: latest.taken_on,
      readings: list.length,
      firstIn: Number(first.forecast_in) || 0,
      latestIn: Number(latest.forecast_in) || 0,
      firstOut: Number(first.forecast_out) || 0,
      latestOut: Number(latest.forecast_out) || 0,
      firstClosing: Number(first.closing) || 0,
      latestClosing: Number(latest.closing) || 0,
      inMoved: (Number(latest.forecast_in) || 0) - (Number(first.forecast_in) || 0),
      outMoved: (Number(latest.forecast_out) || 0) - (Number(first.forecast_out) || 0),
      closingMoved: (Number(latest.closing) || 0) - (Number(first.closing) || 0),
    });
  }
  return out.sort((a, b) => (a.week < b.week ? -1 : 1));
}

/**
 * What a week was forecast to do, against what it did.
 *
 * Keyed on lead time — the forecast made a week out is a different claim from
 * the one made the morning of, and averaging them would flatter both.
 */
export function outturn(snapshots = [], weeks = []) {
  const actual = new Map();
  for (const w of weeks) {
    if (!w.isHistory) continue;
    actual.set(isoDate(w.start), w);
  }
  const rows = [];
  for (const [week, list] of byWeek(snapshots)) {
    const real = actual.get(week);
    if (!real) continue;
    // The last thing we believed before the week began.
    const before = list.filter((s) => s.taken_on < week);
    if (!before.length) continue;
    const call = before[before.length - 1];
    rows.push({
      week,
      leadDays: days(week, call.taken_on),
      forecastIn: Number(call.forecast_in) || 0,
      forecastOut: Number(call.forecast_out) || 0,
      actualIn: real.in,
      actualOut: real.out,
      inError: real.in - (Number(call.forecast_in) || 0),
      outError: real.out - (Number(call.forecast_out) || 0),
    });
  }
  return rows.sort((a, b) => (a.week < b.week ? 1 : -1));
}

/** One number for "can we rely on it", once there is enough to say. */
export function reliability(rows = []) {
  if (!rows.length) return null;
  const pct = (err, base) => (Math.abs(base) < 1 ? null : Math.abs(err / base));
  const inErrs = rows.map((r) => pct(r.inError, r.actualIn)).filter((x) => x != null);
  const outErrs = rows.map((r) => pct(r.outError, r.actualOut)).filter((x) => x != null);
  const mean = (xs) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null);
  return {
    weeks: rows.length,
    inError: mean(inErrs),
    outError: mean(outErrs),
    // Bias matters separately from size: consistently over-forecasting income
    // is a different fault from being noisy about it.
    inBias: rows.reduce((s, r) => s + r.inError, 0) / rows.length,
    outBias: rows.reduce((s, r) => s + r.outError, 0) / rows.length,
  };
}
